import Fbo from 'nanogl/fbo'
import RenderBuffer from 'nanogl/renderbuffer'
import Texture2D from 'nanogl/texture-2d'
import { GLContext, isWebgl2 } from 'nanogl/types'
import { FormatDesc } from 'nanogl-pf'

export default class MsaaFbo {
  msaaFbo: Fbo | undefined = undefined
  blitFbo: Fbo | undefined = undefined

  useBlitFBO = false

  private _msaa = 1
  private _maxMsaa = 1
  private _width = 4
  private _height = 4

  set msaa(samples: number) {
    if (this._msaa !== samples) {
      this._msaa = Math.min(samples, this._maxMsaa)
      this._allocate()
    }
  }

  get msaa(): number {
    return this._msaa
  }

  constructor(
    readonly gl: GLContext,
    readonly pixelFormat: FormatDesc,
    public samples = 4,
    readonly _depth = true,
    readonly _stencil = false
  ) {
    this._maxMsaa = isWebgl2(gl) ? gl.getParameter(gl.MAX_SAMPLES) : 0
    this.msaa = samples
    this._allocate()
  }

  _allocate() {
    const gl = this.gl

    const hasDepthStencil = this._depth

    const depthBindingPoint = gl.DEPTH_STENCIL_ATTACHMENT
    const depthFormat = hasDepthStencil && isWebgl2(gl) ? gl.DEPTH24_STENCIL8 : gl.DEPTH_COMPONENT16

    if (isWebgl2(gl) && this._msaa > 0 && this.msaaFbo === undefined) {
      this.msaaFbo = new Fbo(gl)
      this.msaaFbo.attach(gl.COLOR_ATTACHMENT0, new RenderBuffer(gl, this.pixelFormat.internal, this._msaa))

      if (this._depth) {
        this.msaaFbo.attach(depthBindingPoint, new RenderBuffer(gl, depthFormat, this._msaa))
      }

      this.msaaFbo.resize(this._width, this._height)
    }

    if (this._msaa < 1) {
      this.msaaFbo?.dispose()
      this.msaaFbo = undefined
    }

    if (this.blitFbo === undefined) {
      this.blitFbo = new Fbo(gl)
      this.blitFbo.attach(
        gl.COLOR_ATTACHMENT0,
        new Texture2D(gl, this.pixelFormat.format, this.pixelFormat.type, this.pixelFormat.internal)
      )

      if (this._depth) {
        this.blitFbo.attach(depthBindingPoint, new RenderBuffer(gl, depthFormat))
      }

      this.blitFbo.getColorTexture(0).setFilter(false, false, true)
      this.blitFbo.resize(this._width, this._height)
    }
  }

  get renderFbo() {
    return this.msaaFbo && !this.useBlitFBO ? this.msaaFbo : this.blitFbo
  }

  blitMsaa() {
    if (this.msaaFbo && !this.useBlitFBO) {
      const gl: WebGL2RenderingContext = this.gl as WebGL2RenderingContext

      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.msaaFbo.fbo)
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.blitFbo!.fbo)
      gl.clearBufferfv(gl.COLOR, 0, [0.0, 0.0, 0.0, 1.0])
      gl.clearBufferfv(gl.DEPTH, 0, [0.0, 0.0, 0.0, 1.0])

      let bits = gl.COLOR_BUFFER_BIT as number

      if (this._depth) bits = bits | gl.DEPTH_BUFFER_BIT
      if (this._stencil) bits = bits | gl.STENCIL_BUFFER_BIT

      gl.blitFramebuffer(
        0,
        0,
        this.msaaFbo.width,
        this.msaaFbo.height,
        0,
        0,
        this.msaaFbo.width,
        this.msaaFbo.height,
        bits,
        gl.NEAREST
      )
    }
  }

  defaultViewport() {
    const gl = this.gl
    gl.viewport(0, 0, this._width, this._height)
  }

  getColorTexture(): Texture2D {
    return this.blitFbo!.getColorTexture()
  }

  setSize(w: number, h: number): void {
    this._width = w
    this._height = h
    this.msaaFbo?.resize(w, h)
    this.blitFbo!.resize(w, h)
  }
}
